import React, { useState, useEffect, useRef } from 'react';
import { experimentalStyled as styled } from '@material-ui/core/styles';

const ButtonWrapper = styled('a')(({ theme, $shouldExpand, $isDragging }) => ({
  position: 'fixed',
  right: 0,
  zIndex: 9999,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexDirection: 'row-reverse',
  width: $shouldExpand ? 124 : 46,
  height: 46,
  borderRadius: '24px 0 0 24px',
  textDecoration: 'none',
  color: '#FFFFFF',
  fontFamily: theme.typography.fontFamily || 'Inter, sans-serif',
  fontSize: '0.875rem',
  fontWeight: 600,
  letterSpacing: '0.3px',
  background: 'linear-gradient(135deg, #0072FF 0%, #00C6FF 100%)',
  boxShadow: $shouldExpand
    ? '0 12px 28px rgba(0, 114, 255, 0.5)'
    : '0 8px 20px rgba(0, 114, 255, 0.35)',
  border: '1px solid rgba(255, 255, 255, 0.2)',
  transition: $isDragging 
    ? 'width 0.35s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.35s' // NO top transition while dragging for instant response
    : 'width 0.35s cubic-bezier(0.4, 0, 0.2, 1), transform 0.35s, box-shadow 0.35s',
  cursor: $isDragging ? 'grabbing' : 'grab',
  userSelect: 'none',
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  transform: 'translateY(-50%)',
  animation: 'fadeIn 0.5s ease-out both',

  '& svg': {
    flexShrink: 0,
    marginRight: 10,
    transition: 'transform 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
    transform: $shouldExpand ? 'rotate(12deg) scale(1.05)' : 'none'
  },

  '& .support-text': {
    opacity: $shouldExpand ? 1 : 0,
    maxWidth: $shouldExpand ? 70 : 0,
    marginRight: $shouldExpand ? 8 : 0,
    overflow: 'hidden',
    transition: 'all 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
    display: 'inline-block'
  },

  '@keyframes fadeIn': {
    '0%': { opacity: 0 },
    '100%': { opacity: 1 }
  }
}));

export default function SupportButton() {
  const [isHovered, setIsHovered] = useState(false);
  const [topPos, setTopPos] = useState(
    typeof window !== 'undefined' ? window.innerHeight / 2 : 500
  );
  const [isDragging, setIsDragging] = useState(false);
  
  const dragInfo = useRef({
    startY: 0,
    startTop: 0,
    isDragging: false,
    hasDragged: false
  });

  const handleMouseDown = (e) => {
    // Only left click
    if (e.button !== 0 && e.type !== 'touchstart') return;
    
    dragInfo.current = {
      startY: e.clientY || (e.touches && e.touches[0].clientY),
      startTop: topPos,
      isDragging: true,
      hasDragged: false
    };
    setIsDragging(true);
  };

  useEffect(() => {
    const handleMouseUp = () => {
      if (dragInfo.current.isDragging) {
        dragInfo.current.isDragging = false;
        setIsDragging(false);
      }
    };

    const handleMouseMove = (e) => {
      if (!dragInfo.current.isDragging) return;
      
      // If dragging with mouse but the button is released outside the window
      if (e.type === 'mousemove' && e.buttons !== 1) {
        handleMouseUp();
        return;
      }
      
      const clientY = e.clientY || (e.touches && e.touches[0].clientY);
      const deltaY = clientY - dragInfo.current.startY;
      
      if (Math.abs(deltaY) > 3) {
        dragInfo.current.hasDragged = true;
      }
      
      let newTop = dragInfo.current.startTop + deltaY;
      // Clamp to window bounds (button is 46px high, center is 23px)
      const maxTop = window.innerHeight - 23;
      if (newTop < 23) newTop = 23;
      if (newTop > maxTop) newTop = maxTop;
      
      setTopPos(newTop);
    };

    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      window.addEventListener('touchmove', handleMouseMove, { passive: false });
      window.addEventListener('touchend', handleMouseUp);
    }
    
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('touchmove', handleMouseMove);
      window.removeEventListener('touchend', handleMouseUp);
    };
  }, [isDragging]);

  const handleClick = (e) => {
    if (dragInfo.current.hasDragged) {
      e.preventDefault();
      dragInfo.current.hasDragged = false;
    }
  };

  return (
    <ButtonWrapper
      id="support-button"
      href="https://supportcenter.skyniche.website/auth/login"
      target="_blank"
      rel="noopener noreferrer"
      $shouldExpand={isHovered && !isDragging}
      $isDragging={isDragging}
      style={{ top: topPos }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onMouseDown={handleMouseDown}
      onTouchStart={handleMouseDown}
      onClick={handleClick}
      draggable={false} // Disable HTML5 drag to prevent the ghost image
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M3 18v-6a9 9 0 0 1 18 0v6" />
        <path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3zM3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z" />
      </svg>
      <span className="support-text">Support</span>
    </ButtonWrapper>
  );
}
